import React, { useState, useEffect } from "react";
import { Calendar, Bell, Star, Flame, ChevronDown } from "lucide-react";
import { mockContests } from "../mock";
import { useNavigate } from "react-router-dom";

const Home = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredScreenshot, setHoveredScreenshot] = useState(null);

  const screenshotData = [
    {
      id: "contests",
      title: "Contest List",
      description:
        "Browse all upcoming contests from major platforms like Codeforces, AtCoder, LeetCode, and more. Never miss an opportunity to compete.",
      src: "https://i.ibb.co/9HbzJnHc/image1.png",
      alt: "Contest Hub - Contest List",
    },
    {
      id: "streaks",
      title: "Streaks",
      description:
        "Track your consistency and build unbreakable coding streaks. Visualize your long-term progress and stay motivated.",
      src: "https://i.ibb.co/1GxtWs6F/image4.png",
      alt: "Contest Hub - Streak Tracking",
    },
    {
      id: "bookmarks",
      title: "Bookmark and Reminder",
      description:
        "Save contests for later and set custom reminders. Plan your contests strategically and never be caught off-guard.",
      src: "https://i.ibb.co/67cqCLYS/image5.png",
      alt: "Contest Hub - Insights",
    },
  ];

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const scrollToFeatures = () => {
    document.getElementById("features")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="contest-hub-page">
      {/* Navigation */}
      <nav className="nav-header">
        <div className="nav-content">
          <div className="nav-logo">
            <img
              src="https://i.ibb.co/7xW98Xf7/logo.png"
              alt="Contest Hub"
              className="nav-logo-img"
            />
          </div>
          <div className="nav-links">
            <a href="#features" className="nav-link">
              Features
            </a>
            <a href="#platforms" className="nav-link">
              Platforms
            </a>
            <a href="#privacy" className="nav-link">
              Privacy
            </a>
          </div>
          <a
            href="https://forms.gle/WddJAzUPe9uJodcB6"
            className="btn-nav-download"
            target="_blank"
            rel="noopener noreferrer"
          >
            Join Early Access
          </a>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className={`hero-content ${isVisible ? "fade-in" : ""}`}>
          <h1 className="hero-title">
            You're still coding.
            <br />
            <span style={{ color: "#1A3617" }}>
              That matters.
              <svg
                className="handwritten-underline"
                viewBox="0 0 200 12"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2 8 Q 50 4, 100 6 T 198 8"
                  stroke="#1A3617"
                  strokeWidth="2.5"
                  fill="none"
                  strokeLinecap="round"
                  opacity="0.35"
                />
              </svg>
            </span>
          </h1>
          <p className="hero-subtitle">
            A calm, focused space for competitive programmers.
            <br />
            Track upcoming contests, stay consistent, and build your coding
            journey — one day at a time.
          </p>
          <div className="hero-ctas">
            <a
              href="https://forms.gle/WddJAzUPe9uJodcB6"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
            >
              Join Early Access
            </a>
            <button onClick={scrollToFeatures} className="btn-secondary">
              Learn more <ChevronDown size={16} style={{ marginLeft: "4px" }} />
            </button>
          </div>
          <p className="trust-indicator">Privacy-first · No ads · No noise</p>
        </div>
      </section>

      {/* App Screenshots Section */}
      <section className="screenshots-section">
        <div className="screenshots-container">
          {screenshotData.map((screenshot, index) => (
            <div
              key={screenshot.id}
              className={`screenshot-device-wrapper ${
                hoveredScreenshot === screenshot.id ? "hovered" : ""
              }`}
            >
              <div
                className={`screenshot-device ${index === 0 ? "side-device" : index === 1 ? "center-device" : "side-device"}`}
                onMouseEnter={() => setHoveredScreenshot(screenshot.id)}
                onMouseLeave={() => setHoveredScreenshot(null)}
              >
                <div className="screenshot-image-wrapper">
                  <img
                    src={screenshot.src}
                    alt={screenshot.alt}
                    className="screenshot-img"
                  />
                  <div className="screenshot-highlight" />
                  <div className="screenshot-overlay">
                    <span className="screenshot-label">{screenshot.title}</span>
                  </div>
                </div>
              </div>
              <div className="screenshot-info">
                <p className="screenshot-description">
                  {screenshot.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Value Proposition */}
      <section className="value-section">
        <div className="value-content">
          <h2 className="section-title">A calmer way to stay competitive</h2>
          <p className="section-description">
            Contest Hub removes noise and helps you focus on what actually
            improves you — consistency.
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">
              <Calendar size={24} strokeWidth={1.5} />
            </div>
            <h3 className="feature-title">All Upcoming Contests</h3>
            <p className="feature-description">
              View competitive coding contests from all major platforms in one
              place.
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <Bell size={24} strokeWidth={1.5} />
            </div>
            <h3 className="feature-title">Gentle Reminders</h3>
            <p className="feature-description">
              Get notified before contests start. No spam.
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <Star size={24} strokeWidth={1.5} />
            </div>
            <h3 className="feature-title">Bookmark What Matters</h3>
            <p className="feature-description">Save contests and plan ahead.</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <Flame size={24} strokeWidth={1.5} />
            </div>
            <h3 className="feature-title">Streak Tracking</h3>
            <p className="feature-description">
              Visualize your consistency and long-term progress.
            </p>
          </div>
        </div>
      </section>

      {/* Motivation Section */}
      <section className="motivation-section">
        <div className="motivation-content">
          <h2 className="section-title">
            Progress isn't loud. It's consistent.
          </h2>
          <p className="section-description">
            Contest Hub is built for programmers who show up quietly, every day.
          </p>
          <div className="streak-visual">
            <div className="streak-carousel">
              <div className="streak-item">
                <img
                  src="https://i.ibb.co/KchLTMWr/notification.png"
                  alt="Streak Tracking"
                  className="streak-image"
                />
                <p className="streak-label">Track Your Streaks</p>
                <p className="streak-description">
                  Build unbreakable streaks and visualize your consistency.
                </p>
              </div>
              <div className="streak-item">
                <img
                  src="https://i.ibb.co/W4JmfyWq/image2.png"
                  alt="Reminders"
                  className="streak-image"
                />
                <p className="streak-label">Smart Reminders</p>
                <p className="streak-description">
                  Get gentle notifications before contests start.
                </p>
              </div>
              <div className="streak-item">
                <img
                  src="https://i.ibb.co/r284G1j8/image3.png"
                  alt="Progress Analytics"
                  className="streak-image"
                />
                <p className="streak-label">Analyze Progress</p>
                <p className="streak-description">
                  Deep insights into your competitive coding journey.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Availability */}
      <section id="platforms" className="platforms-section">
        <div className="platforms-grid">
          <div className="platform-card active">
            <h3 className="platform-name">Android</h3>
            <p className="platform-status">Available now</p>
            <a
              href="https://forms.gle/WddJAzUPe9uJodcB6"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-platform"
            >
              Join Early Access
            </a>
          </div>
          <div className="platform-card inactive">
            <h3 className="platform-name">iOS</h3>
            <p className="platform-status">Coming soon</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="privacy" className="footer">
        <div className="footer-content">
          <div className="footer-main">
            <img
              src="https://i.ibb.co/7xW98Xf7/logo.png"
              alt="Contest Hub"
              className="footer-logo"
            />
            <p className="footer-tagline">
              A calm, focused space for competitive programmers.
            </p>
            <a
              href="https://forms.gle/WddJAzUPe9uJodcB6"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-footer-download"
            >
              Join Early Access
            </a>
            <p className="footer-ios-note">iOS coming soon</p>
          </div>
          <div className="footer-links">
            <a href="mailto:champ96klabs@gmail.com" className="footer-link">
              champ96klabs@gmail.com
            </a>
            <span className="footer-divider">·</span>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                navigate("/privacy-policy");
              }}
              className="footer-link"
            >
              Privacy Policy
            </a>
            <span className="footer-divider">·</span>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                navigate("/about-us");
              }}
              className="footer-link"
            >
              About Us
            </a>
            <span className="footer-divider">·</span>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                navigate("/terms-and-conditions");
              }}
              className="footer-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms &amp; Conditions
            </a>
          </div>
          <div className="footer-credit">
            <p className="footer-credit-text">
              Developed with passion by{" "}
              <a
                href="https://labs.champ96k.com"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-credit-link"
              >
                Champ96k Labs
              </a>
              {" · "}an indie developer platform by{" "}
              <a
                href="https://labs.champ96k.com"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-credit-link"
              >
                Tushar Nikam
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
